// texatlasmgr.ts ↔ game/texatlasmgr.cpp — the texture-atlas manager.
//
// Packs carry atlasmap.json (sprite name → atlas coords/crop/baseline/per-side
// atlases or team-color mask decal) + texture<N>.png atlas images. The 24bpp
// (2432) pack uses 3 textures (base 0/1 + mask-decal 2); the baked 8bpp (832)
// pack uses 6 (base 0 + per-side 1..5) — counts are data-driven from the map.
// yajl JSON parsing collapses to JSON.parse (scope-excluded); image decode is the
// platform's (browser-native in production; the test harness brings its own PNG
// decoder). BltTo/BltGlyphTinted geometry is ported here against a DibBitmap
// seam whose pixel ops land with the M2 renderer.

import { TBitmap } from './tbitmap.ts';
import { GetSideColor as paletteSideColor } from './palette.ts';
import type { PackFileReader } from '../io/packfile.ts';
import type { PixelSource } from './dibbitmap.ts';

export type Side = number;
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
export interface Color {
  r: number;
  g: number;
  b: number;
}

/** The renderer seam (M2 DibBitmap): atlas-subrect copy + the additive
 *  team-color decal + the glyph color-modulate. */
export interface DibBitmapLike {
  // The blit core only reads cx/cy/rgba, so a PixelSource (an in-memory bitmap such as
  // CpuDibBitmap's Flash() copy, which has no `decal` flag) is a valid source too — the
  // concrete CpuDibBitmap already declares this union. Type-only; widens the accepted arg.
  /** `opacity` (0-255, default 255=opaque) fades the whole blit — a cosmetic per-sprite
   *  alpha (the projectile after-image trail). 255 leaves every existing call byte-identical. */
  Blt(
    src: AtlasImage | PixelSource,
    prcSrc: Rect,
    xDst: number,
    yDst: number,
    opacity?: number,
  ): void;
  BltTinted(
    src: AtlasImage | PixelSource,
    prcSrc: Rect,
    xDst: number,
    yDst: number,
    clr: Color,
    opacity?: number,
  ): void;
  /** Glyph recolor: modulate (BLENDMODE_BLEND with ColorMod), NOT additive —
   *  texatlasmgr.cpp:320-344's SDL_SetTextureColorMod-around-Blt becomes an op. */
  BltModulated(
    src: AtlasImage | PixelSource,
    prcSrc: Rect,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void;
}

/** A decoded atlas image (the platform supplies pixels; tests use harness PNG). */
export interface AtlasImage {
  cx: number;
  cy: number;
  rgba: Uint8Array;
  /** true for the mask-decal atlas (loaded via LoadDibBitmapDecal in C++ —
   *  additive-blend texture state; the pixel bytes are unchanged). */
  decal: boolean;
}

export const kcSides = 5; // ht.h — neutral + sides 1..4

interface AtlasMapEntry {
  x: number;
  y: number;
  cx: number;
  cy: number;
  cx_orig: number;
  cy_orig: number;
  cc_left: number;
  cc_top: number;
  yBaseline?: number;
  atlas?: number;
  atlases?: number[];
  mask?: {
    atlas: number;
    x: number;
    y: number;
    cx: number;
    cy: number;
    cc_left: number;
    cc_top: number;
  };
}

const num = (v: unknown): number => (typeof v === 'number' ? v : -1); // GetInteger: -1 when missing

/** bitmap.cpp:164-216 DibBitmap::InitDecal — the mask atlas stores the
 *  side-colorable region baked in side-1 blue; rewrite to a TINTABLE form:
 *  RGB forced to white, alpha = max(r,g,b) * a / 255 (intensity-scaled
 *  coverage). BltTinted's per-side ColorMod + ADD then recolors it. */
export function decalTransform(rgba: Uint8Array): void {
  for (let i = 0; i < rgba.length; i += 4) {
    const r = rgba[i],
      g = rgba[i + 1],
      b = rgba[i + 2],
      a = rgba[i + 3];
    const inten = r > g ? (r > b ? r : b) : g > b ? g : b;
    rgba[i] = 255;
    rgba[i + 1] = 255;
    rgba[i + 2] = 255;
    rgba[i + 3] = Math.floor((inten * a) / 255);
  }
}

export class TexAtlasMgr {
  private m_json: Record<string, AtlasMapEntry> | null = null;
  private m_pbmAtlases: (AtlasImage | null)[] = [];
  private setDegenerate = new Set<string>();

  get natlases(): number {
    return this.m_pbmAtlases.length;
  }
  atlas(i: number): AtlasImage | null {
    return this.m_pbmAtlases[i] ?? null;
  }
  degenerateKeys(): ReadonlySet<string> {
    return this.setDegenerate;
  }

  // game/texatlasmgr.cpp:74-143 — atlas count + mask-atlas index are data-driven;
  // decodePng is the platform image decoder (browser-native in production)
  Init(
    pakr: PackFileReader,
    decodePng: (bytes: Uint8Array) => { cx: number; cy: number; rgba: Uint8Array },
  ): boolean {
    const raw = pakr.MapFile('atlasmap.json');
    if (raw === null) return false;
    const json = JSON.parse(new TextDecoder('latin1').decode(raw)) as Record<string, AtlasMapEntry>;

    let natlases = 1;
    let iatlasMask = -1;
    for (const e of Object.values(json)) {
      const a = num(e.atlas);
      if (a + 1 > natlases) natlases = a + 1;
      if (e.atlases !== undefined) {
        for (const av of e.atlases) if (av + 1 > natlases) natlases = av + 1;
      }
      if (e.mask !== undefined) {
        iatlasMask = e.mask.atlas;
        if (iatlasMask + 1 > natlases) natlases = iatlasMask + 1;
      }
    }

    this.m_pbmAtlases = [];
    for (let i = 0; i < natlases; i++) {
      const bytes = pakr.MapFile(`texture${i}.png`);
      if (bytes === null) return false;
      this.m_pbmAtlases.push({ ...decodePng(bytes), decal: i === iatlasMask });
    }

    this.m_json = json;
    if (iatlasMask >= 0) {
      // ORDER MATTERS: the degenerate scan reads the RAW mask pixels (the C++
      // re-loads texture2 from the pack for it, texatlasmgr.cpp:156-177), and
      // only the blit path uses the decal-transformed form.
      this.ScanDegenerateMasks(iatlasMask);
      decalTransform(this.m_pbmAtlases[iatlasMask]!.rgba);
    }
    return true;
  }

  // game/texatlasmgr.cpp:145-224 — flag placeholder frames whose team-color mask
  // is a fully-opaque uniform block (the recovered lost-art frames): tinted they
  // would paint as a solid color block, so BltTo skips them entirely.
  private ScanDegenerateMasks(iatlasMask: number): void {
    const surf = this.m_pbmAtlases[iatlasMask];
    if (surf === null || this.m_json === null) return;
    const px = surf.rgba;
    const row = surf.cx;
    for (const [key, e] of Object.entries(this.m_json)) {
      const m = e.mask;
      if (m === undefined) continue;
      const { x: mx, y: my, cx: mcx, cy: mcy } = m;
      if (mcx <= 0 || mcy <= 0) continue;
      if (mx < 0 || my < 0 || mx + mcx > surf.cx || my + mcy > surf.cy) continue;
      const f = (my * row + mx) * 4;
      const fr = px[f],
        fg = px[f + 1],
        fb = px[f + 2];
      let degenerate = px[f + 3] >= 200;
      for (let yy = 0; degenerate && yy < mcy; yy++) {
        for (let xx = 0; xx < mcx; xx++) {
          const i = ((my + yy) * row + (mx + xx)) * 4;
          if (px[i + 3] < 200 || px[i] !== fr || px[i + 1] !== fg || px[i + 2] !== fb) {
            degenerate = false;
            break;
          }
        }
      }
      if (degenerate) this.setDegenerate.add(key);
    }
  }

  // game/texatlasmgr.cpp:346-459
  CreateTBitmap(pszName: string): TBitmap | null {
    if (this.m_json === null) return null;
    const szName = pszName.toLowerCase();
    const entry = this.m_json[szName];
    if (entry === undefined) {
      // Scenery/cutscene names are "hardcoded" into level files as .tbm/.rbm;
      // retry as .png (texatlasmgr.cpp:360-379)
      const dot = szName.lastIndexOf('.');
      if (dot >= 0) {
        const suffix = szName.slice(dot);
        if (suffix === '.tbm' || suffix === '.rbm') {
          return this.CreateTBitmap(szName.slice(0, dot) + '.png');
        }
      }
      return null;
    }

    // Per-side coloring: 832-baked packs carry "atlases" (one atlas per side);
    // 2432 carries a single "atlas" for all sides (+ optional mask decal)
    const anSideMap: number[] = [];
    if (entry.atlases !== undefined) {
      const cAtlas = entry.atlases.length;
      for (let i = 0; i < kcSides; i++) {
        anSideMap.push(entry.atlases[i < cAtlas ? i : cAtlas - 1]);
      }
    } else {
      const iatlas = num(entry.atlas);
      for (let i = 0; i < kcSides; i++) anSideMap.push(iatlas);
    }

    let yBaseline = num(entry.yBaseline);
    if (yBaseline < 0) yBaseline = 0;

    const ptbm = new TBitmap(this);
    if (
      !ptbm.Init(
        szName,
        num(entry.x),
        num(entry.y),
        num(entry.cx),
        num(entry.cy),
        num(entry.cx_orig),
        num(entry.cy_orig),
        num(entry.cc_left),
        num(entry.cc_top),
        yBaseline,
        anSideMap,
      )
    ) {
      return null;
    }

    if (this.setDegenerate.has(szName)) {
      ptbm.SetDegenerate();
    } else if (entry.mask !== undefined) {
      const m = entry.mask;
      ptbm.SetMask(m.atlas, m.x, m.y, m.cx, m.cy, m.cc_left, m.cc_top);
    }
    return ptbm;
  }

  // game/texatlasmgr.cpp:226-318 — the source-rect geometry for base + mask blits
  BltTo(
    ptbmSrc: TBitmap,
    pbmDst: DibBitmapLike,
    xDst: number,
    yDst: number,
    side: Side,
    prcSrc: Rect | null,
    opacity = 255,
  ): void {
    if (ptbmSrc.IsDegenerate()) return;

    const siz = ptbmSrc.GetTextureSize();
    const pt = ptbmSrc.GetPosition();

    const rc: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
    if (prcSrc !== null) {
      rc.left = pt.x + prcSrc.left - ptbmSrc.ClippedLeft();
      rc.top = pt.y + prcSrc.top - ptbmSrc.ClippedTop();
      rc.right = rc.left + (prcSrc.right - prcSrc.left);
      rc.bottom = rc.top + (prcSrc.bottom - prcSrc.top);
      if (rc.left < pt.x) rc.left = pt.x;
      if (rc.top < pt.y) rc.top = pt.y;
      if (rc.right > pt.x + siz.cx) rc.right = pt.x + siz.cx;
      if (rc.bottom > pt.y + siz.cy) rc.bottom = pt.y + siz.cy;
    } else {
      rc.left = pt.x;
      rc.top = pt.y;
      rc.right = pt.x + siz.cx;
      rc.bottom = pt.y + siz.cy;
    }

    const atlas = this.m_pbmAtlases[ptbmSrc.GetAtlas(side)];
    if (atlas) {
      pbmDst.Blt(atlas, rc, xDst + ptbmSrc.ClippedLeft(), yDst + ptbmSrc.ClippedTop(), opacity);
    }

    // Team color: additive mask decal tinted with the side color (2432 scheme)
    if (ptbmSrc.HasMask() && this.m_pbmAtlases[ptbmSrc.MaskAtlas()]) {
      const ptm = { x: ptbmSrc.MaskX(), y: ptbmSrc.MaskY() };
      const sizm = { cx: ptbmSrc.MaskCx(), cy: ptbmSrc.MaskCy() };
      const rcm: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
      if (prcSrc !== null) {
        rcm.left = ptm.x + prcSrc.left - ptbmSrc.MaskClippedLeft();
        rcm.top = ptm.y + prcSrc.top - ptbmSrc.MaskClippedTop();
        rcm.right = rcm.left + (prcSrc.right - prcSrc.left);
        rcm.bottom = rcm.top + (prcSrc.bottom - prcSrc.top);
        if (rcm.left < ptm.x) rcm.left = ptm.x;
        if (rcm.top < ptm.y) rcm.top = ptm.y;
        if (rcm.right > ptm.x + sizm.cx) rcm.right = ptm.x + sizm.cx;
        if (rcm.bottom > ptm.y + sizm.cy) rcm.bottom = ptm.y + sizm.cy;
      } else {
        rcm.left = ptm.x;
        rcm.top = ptm.y;
        rcm.right = ptm.x + sizm.cx;
        rcm.bottom = ptm.y + sizm.cy;
      }
      if (rcm.right - rcm.left > 0 && rcm.bottom - rcm.top > 0) {
        pbmDst.BltTinted(
          this.m_pbmAtlases[ptbmSrc.MaskAtlas()]!,
          rcm,
          xDst + ptbmSrc.MaskClippedLeft(),
          yDst + ptbmSrc.MaskClippedTop(),
          paletteSideColor(side),
          opacity,
        );
      }
    }
  }

  // game/texatlasmgr.cpp:326-344 — glyphs are baked white-on-transparent; a
  // requested color modulates them. The C++ sets SDL texture ColorMod state
  // around the normal BltTo; here the same geometry routes Blt → BltModulated.
  BltGlyphTinted(
    ptbmSrc: TBitmap,
    pbmDst: DibBitmapLike,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void {
    const side: Side = 1; // texatlasmgr.cpp:329
    const proxy: DibBitmapLike = {
      Blt: (src, rc, x, y) => pbmDst.BltModulated(src, rc, x, y, clr),
      BltTinted: (src, rc, x, y, c) => pbmDst.BltTinted(src, rc, x, y, c),
      BltModulated: (src, rc, x, y, c) => pbmDst.BltModulated(src, rc, x, y, c),
    };
    this.BltTo(ptbmSrc, proxy, xDst, yDst, side, null);
  }
}

// Side → team color now lives in palette.ts (the extracted gaclr24bpp +
// gaiclrSide tables) — re-exported for BltTo's callers.
export { GetSideColor } from './palette.ts';
